import {
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Input } from '@heroui/input';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from '@heroui/modal';
import { Pagination } from '@heroui/pagination';
import { Select, SelectItem } from '@heroui/select';
import { Spinner } from '@heroui/spinner';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { useLocale } from '../../contexts/LocaleContext';
import { orderService } from '../../services/orderService';
import { Order, OrderStatusObject } from '../../types/orders';

const Orders: React.FC = () => {
  const pageTitle = 'My Orders';
  const pageDescription = 'View your order history and current order status.';
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatusObject[]>([]);
  const { formatCurrency, convertPrice } = useLocale();

  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure();
  const {
    isOpen: isShippingOpen,
    onOpen: onShippingOpen,
    onClose: onShippingClose,
  } = useDisclosure();

  const [shippingForm, setShippingForm] = useState({
    shippingName: '',
    shippingAddress: '',
    shippingCity: '',
    shippingState: '',
    shippingZipCode: '',
    shippingCountry: '',
    shippingPhoneNumber: '',
  });

  const fetchOrderStatuses = useCallback(async () => {
    try {
      const statuses = await orderService.getOrderStatuses();
      // Add "All Statuses" option at the beginning
      const statusOptions = [{ id: '', name: 'All Statuses' }, ...statuses];
      setOrderStatuses(statusOptions);
    } catch (error) {
      toast.error('Failed to load order statuses');
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await orderService.getOrders({
        orderNumber: searchTerm || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        pageSize: 10,
      });

      setOrders(response.orders);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      toast.error('Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  useEffect(() => {
    fetchOrderStatuses();
  }, [fetchOrderStatuses]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders();
  };

  const handleViewDetails = async (orderId: number) => {
    try {
      const order = await orderService.getOrder(orderId);
      setSelectedOrder(order);
      onDetailsOpen();
    } catch (error: any) {
      toast.error('Failed to fetch order details');
    }
  };

  const handleEditShipping = (order: Order) => {
    setSelectedOrder(order);
    setShippingForm({
      shippingName: order.shippingName,
      shippingAddress: order.shippingAddress,
      shippingCity: order.shippingCity,
      shippingState: order.shippingState,
      shippingZipCode: order.shippingZipCode,
      shippingCountry: order.shippingCountry,
      shippingPhoneNumber: order.shippingPhoneNumber || '',
    });
    onShippingOpen();
  };

  const handleUpdateShipping = async () => {
    if (!selectedOrder) return;

    const phone =
      shippingForm.shippingPhoneNumber?.trim() ||
      selectedOrder.shippingPhoneNumber ||
      '';
    if (!phone) {
      toast.error('Phone number is required for Royal Mail tracking');
      return;
    }

    try {
      const payload = {
        ...shippingForm,
        shippingCountry: 'GB',
        shippingPhoneNumber: phone,
      };
      await orderService.updateShippingInfo(selectedOrder.id, payload);
      toast.success('Shipping information updated successfully');
      onShippingClose();
      fetchOrders();
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to update shipping information'
      );
    }
  };

  const getStatusColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'primary';
      case 'printing':
        return 'secondary';
      case 'printed':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'refunded':
        return 'danger';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return <ClockIcon className='w-4 h-4' />;
      case 'processing':
        return <ClockIcon className='w-4 h-4' />;
      case 'printing':
        return <ClockIcon className='w-4 h-4' />;
      case 'printed':
        return <CheckCircleIcon className='w-4 h-4' />;
      case 'shipped':
        return <TruckIcon className='w-4 h-4' />;
      case 'delivered':
        return <CheckCircleIcon className='w-4 h-4' />;
      case 'cancelled':
        return <XCircleIcon className='w-4 h-4' />;
      case 'refunded':
        return <XCircleIcon className='w-4 h-4' />;
      default:
        return <ClockIcon className='w-4 h-4' />;
    }
  };

  const canEditShipping = (status: string) => {
    return ['pending', 'processing'].includes(status.toLowerCase());
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='space-y-6'>
        <Card>
          <CardHeader>
            <div className='w-full'>
              <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
                <div>
                  <h2 className='text-xl font-semibold'>My Orders</h2>
                  <p className='text-gray-600'>Track and manage your orders</p>
                </div>
              </div>

              <div className='flex flex-col sm:flex-row gap-4 mt-6'>
                <div className='flex-1'>
                  <Input
                    placeholder='Search by order number...'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleSearch()}
                    startContent={
                      <MagnifyingGlassIcon className='w-4 h-4 text-gray-400' />
                    }
                  />
                </div>
                <Select
                  placeholder='Filter by status'
                  className='w-full sm:w-48'
                  selectedKeys={statusFilter ? [statusFilter] : []}
                  items={orderStatuses}
                  onSelectionChange={keys => {
                    const selected = Array.from(keys)[0] as string;
                    setStatusFilter(selected || '');
                  }}
                >
                  {option => (
                    <SelectItem key={option.id}>{option.name}</SelectItem>
                  )}
                </Select>
                <Button color='primary' onClick={handleSearch}>
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {isLoading ? (
              <div className='flex justify-center py-8'>
                <Spinner size='lg' />
              </div>
            ) : orders.length === 0 ? (
              <div className='text-center py-8'>
                <p className='text-gray-500'>No orders found</p>
              </div>
            ) : (
              <div className='space-y-4'>
                {orders.map(order => (
                  <Card key={order.id} className='border'>
                    <CardBody>
                      <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4'>
                        <div className='flex-1'>
                          <div className='flex items-center space-x-3 mb-2'>
                            <h3 className='font-semibold'>
                              Order #{order.orderNumber}
                            </h3>
                            <Chip
                              color={getStatusColor(order.status)}
                              variant='flat'
                              startContent={getStatusIcon(order.status)}
                              size='sm'
                            >
                              {order.status}
                            </Chip>
                          </div>

                          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-sm'>
                            <div>
                              <p className='text-gray-500'>Order Date</p>
                              <p className='font-medium'>
                                {new Date(order.orderDate).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <p className='text-gray-500'>Total Amount</p>
                              <p className='font-medium'>
                                {formatAmount(order.totalAmount)}
                              </p>
                            </div>
                            <div>
                              <p className='text-gray-500'>Items</p>
                              <p className='font-medium'>
                                {order.items.length} item(s)
                              </p>
                            </div>
                          </div>

                          {order.trackingNumber && (
                            <div className='mt-3 p-3 bg-blue-50 rounded-lg'>
                              <p className='text-sm font-medium text-blue-900'>
                                Tracking: {order.trackingNumber}
                              </p>
                              {order.shippedDate && (
                                <p className='text-xs text-blue-700 mt-1'>
                                  Shipped on{' '}
                                  {new Date(
                                    order.shippedDate
                                  ).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        <div className='flex flex-col sm:flex-row gap-2'>
                          <Button
                            size='sm'
                            variant='bordered'
                            onClick={() => handleViewDetails(order.id)}
                            startContent={<EyeIcon className='w-4 h-4' />}
                          >
                            Details
                          </Button>
                          {canEditShipping(order.status) && (
                            <Button
                              size='sm'
                              variant='bordered'
                              color='primary'
                              onClick={() => handleEditShipping(order)}
                              startContent={<TruckIcon className='w-4 h-4' />}
                            >
                              Edit Shipping
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className='flex justify-center mt-6'>
                <Pagination
                  total={totalPages}
                  page={currentPage}
                  onChange={setCurrentPage}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Order Details Modal */}
        <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size='2xl'>
          <ModalContent>
            <ModalHeader>
              <h3>Order Details - #{selectedOrder?.orderNumber}</h3>
            </ModalHeader>
            <ModalBody>
              {selectedOrder && (
                <div className='space-y-6'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <p className='text-sm text-gray-500'>Status</p>
                      <Chip
                        color={getStatusColor(selectedOrder.status)}
                        variant='flat'
                        startContent={getStatusIcon(selectedOrder.status)}
                      >
                        {selectedOrder.status}
                      </Chip>
                    </div>
                    <div>
                      <p className='text-sm text-gray-500'>Order Date</p>
                      <p className='font-medium'>
                        {new Date(selectedOrder.orderDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h4 className='font-semibold mb-3'>Items</h4>
                    <div className='space-y-3'>
                      {selectedOrder.items.map(item => (
                        <div
                          key={item.id}
                          className='flex justify-between items-center p-3 border rounded-lg'
                        >
                          <div>
                            <p className='font-medium'>{item.productName}</p>
                            <p className='text-sm text-gray-500'>
                              Quantity: {item.quantity}
                            </p>
                          </div>
                          <p className='font-medium'>
                            {formatAmount(item.totalPrice)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Divider />

                  <div>
                    <h4 className='font-semibold mb-3'>Shipping Address</h4>
                    <div className='p-3 bg-gray-50 rounded-lg'>
                      <p className='font-medium'>
                        {selectedOrder.shippingName}
                      </p>
                      <p>{selectedOrder.shippingAddress}</p>
                      <p>
                        {selectedOrder.shippingCity},{' '}
                        {selectedOrder.shippingState}{' '}
                        {selectedOrder.shippingZipCode}
                      </p>
                      <p>{selectedOrder.shippingCountry}</p>
                    </div>
                  </div>

                  <Divider />

                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <p className='text-sm text-gray-500'>Subtotal</p>
                      <p className='font-medium'>
                        {formatAmount(
                          selectedOrder.totalAmount -
                            selectedOrder.shippingCost -
                            selectedOrder.taxAmount
                        )}
                      </p>
                    </div>
                    <div>
                      <p className='text-sm text-gray-500'>Shipping</p>
                      <p className='font-medium'>
                        {formatAmount(selectedOrder.shippingCost)}
                      </p>
                    </div>
                    <div>
                      <p className='text-sm text-gray-500'>Tax</p>
                      <p className='font-medium'>
                        {formatAmount(selectedOrder.taxAmount)}
                      </p>
                    </div>
                    <div>
                      <p className='text-sm text-gray-500'>Total</p>
                      <p className='font-bold text-lg'>
                        {formatAmount(selectedOrder.totalAmount)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onDetailsClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Edit Shipping Modal */}
        <Modal isOpen={isShippingOpen} onClose={onShippingClose} size='2xl'>
          <ModalContent>
            <ModalHeader>
              <h3>Edit Shipping Information</h3>
            </ModalHeader>
            <ModalBody>
              <div className='space-y-4'>
                <Input
                  label='Full Name'
                  value={shippingForm.shippingName}
                  onChange={e =>
                    setShippingForm(prev => ({
                      ...prev,
                      shippingName: e.target.value,
                    }))
                  }
                />
                <Input
                  label='Address'
                  value={shippingForm.shippingAddress}
                  onChange={e =>
                    setShippingForm(prev => ({
                      ...prev,
                      shippingAddress: e.target.value,
                    }))
                  }
                />
                <div className='grid grid-cols-2 gap-4'>
                  <Input
                    label='City'
                    value={shippingForm.shippingCity}
                    onChange={e =>
                      setShippingForm(prev => ({
                        ...prev,
                        shippingCity: e.target.value,
                      }))
                    }
                  />
                  <Input
                    label='State'
                    value={shippingForm.shippingState}
                    onChange={e =>
                      setShippingForm(prev => ({
                        ...prev,
                        shippingState: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <Input
                    label='ZIP Code'
                    value={shippingForm.shippingZipCode}
                    onChange={e =>
                      setShippingForm(prev => ({
                        ...prev,
                        shippingZipCode: e.target.value,
                      }))
                    }
                  />
                  {/* UK-only for MVP: country locked */}
                  <Input
                    label='Country'
                    value='United Kingdom'
                    isReadOnly
                    isDisabled
                  />
                </div>
                <Input
                  label='Phone Number (required for Royal Mail)'
                  type='tel'
                  value={shippingForm.shippingPhoneNumber}
                  onChange={e =>
                    setShippingForm(prev => ({
                      ...prev,
                      shippingPhoneNumber: e.target.value,
                    }))
                  }
                  isRequired
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant='bordered' onClick={onShippingClose}>
                Cancel
              </Button>
              <Button color='primary' onClick={handleUpdateShipping}>
                Update Shipping
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </>
  );
};

export default Orders;
